import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  EventNote as EventNoteIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  PlayCircleFilled as PlayCircleFilledIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import {
  fetchAppointmentsByDoctor,
  fetchAppointmentById,
  confirmAppointment,
  completeAppointment,
  startVideoCall
} from '../../services/healinApi';
import AppointmentList from '../../components/AppointmentList/AppointmentList';
import AppointmentDetail from '../../components/AppointmentDetail/AppointmentDetail';
import TwilioVideo from '../../components/TwilioVideo/TwilioVideo';
import AppointmentCalendar from '../../components/AppointmentCalendar/AppointmentCalendar';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslations } from '../../translations/translations';

/**
 * PrivateHealthPracticeHub Component
 * 
 * Revamped to provide appointment management for doctors:
 * - View and manage appointment requests
 * - Confirm appointments 
 * - Conduct video consultations
 * - Complete appointments
 */
const PrivateHealthPracticeHub = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sortBy, setSortBy] = useState('date');
  
  // Appointment states
  const [scheduledAppointments, setScheduledAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [ongoingAppointments, setOngoingAppointments] = useState([]);
  const [completedAppointments, setCompletedAppointments] = useState([]);
  
  // Video call state
  const [videoCallAppointment, setVideoCallAppointment] = useState(null);
  const [showVideoCall, setShowVideoCall] = useState(false);
  
  // Appointment detail state
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  
  // Debug: Log user object to see its structure
  console.log('Current user object:', user);

  // Current doctor info from authentication - using same pattern as Chronic Prescriptions
  const currentDoctor = {
    id: user?.username || 'unknown-doctor', // Use same pattern as Chronic Prescriptions
    name: user?.attributes?.name || user?.name || user?.username || 'Doctor',
    specialty: user?.attributes?.['custom:specialty'] || 'Healthcare Provider',
    email: user?.attributes?.email || user?.email || '',
    phone: user?.attributes?.phone_number || user?.phone || ''
  };

  console.log('Current doctor info:', currentDoctor);
  console.log('Doctor ID for API calls:', currentDoctor.id);

  // Data processing functions
  const processScheduledAppointments = useCallback((appointments) => {
    console.log('Processing scheduled appointments:', appointments);
    return appointments
      .filter(appointment => appointment.status?.toUpperCase() === 'SCHEDULED')
      .map(appointment => ({
        ...appointment,
        patientName: appointment.userDetail ? 
          `${appointment.userDetail.FirstName || ''} ${appointment.userDetail.LastName || ''}`.trim() :
          `Patient ${appointment.userId?.slice(-8) || 'Unknown'}`,
        patientInfo: appointment.userDetail ? {
          age: appointment.userDetail.Age,
          gender: appointment.userDetail.Gender,
          email: appointment.userDetail.Email,
          phone: appointment.userDetail.Phone_Number,
          address: appointment.userDetail.Address,
          chronicConditions: appointment.userDetail.ChronicConditions || [],
          allergies: appointment.userDetail.Allergies || []
        } : null
      }));
  }, []);

  // Helper function to get current week bounds (Sunday to Saturday)
  const getCurrentWeekBounds = useCallback(() => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Calculate start of week (Sunday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - currentDay);
    startOfWeek.setHours(0, 0, 0, 0);
    
    // Calculate end of week (Saturday)
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    console.log('Current week bounds:', {
      start: startOfWeek.toISOString(),
      end: endOfWeek.toISOString(),
      startFormatted: startOfWeek.toLocaleDateString(),
      endFormatted: endOfWeek.toLocaleDateString()
    });
    
    return { startOfWeek, endOfWeek };
  }, []);

  const processUpcomingAppointments = useCallback((appointments) => {
    console.log('Processing upcoming appointments:', appointments);
    const { startOfWeek, endOfWeek } = getCurrentWeekBounds();
    
    return appointments
      .filter(appointment => {
        // Include CONFIRMED appointments that fall within current week
        const isConfirmed = appointment.status?.toUpperCase() === 'CONFIRMED';
        
        if (!isConfirmed) return false;
        
        // Check if appointment is in current week
        const appointmentDate = appointment.confirmedSlot ? 
          new Date(appointment.confirmedSlot) : 
          (appointment.scheduledAt ? new Date(appointment.scheduledAt) : null);
        
        if (!appointmentDate) {
          console.log('No valid date found for appointment:', appointment.requestId);
          return false;
        }
        
        const isInCurrentWeek = appointmentDate >= startOfWeek && appointmentDate <= endOfWeek;
        
        console.log('Appointment date check:', {
          appointmentId: appointment.requestId,
          appointmentDate: appointmentDate.toISOString(),
          isInCurrentWeek,
          startOfWeek: startOfWeek.toISOString(),
          endOfWeek: endOfWeek.toISOString()
        });
        
        return isInCurrentWeek;
      })
      .map(appointment => ({
        ...appointment,
        patientName: appointment.userDetail ? 
          `${appointment.userDetail.FirstName || ''} ${appointment.userDetail.LastName || ''}`.trim() :
          `Patient ${appointment.userId?.slice(-8) || 'Unknown'}`,
        patientInfo: appointment.userDetail ? {
          age: appointment.userDetail.Age,
          gender: appointment.userDetail.Gender,
          email: appointment.userDetail.Email,
          phone: appointment.userDetail.Phone_Number,
          address: appointment.userDetail.Address,
          chronicConditions: appointment.userDetail.ChronicConditions || [],
          allergies: appointment.userDetail.Allergies || []
        } : null
      }))
      .sort((a, b) => {
        // Sort by appointment date/time
        const dateA = new Date(a.confirmedSlot || a.scheduledAt || 0);
        const dateB = new Date(b.confirmedSlot || b.scheduledAt || 0);
        return dateA - dateB;
      });
  }, [getCurrentWeekBounds]);

  const processOngoingAppointments = useCallback((appointments) => {
    console.log('Processing ongoing appointments:', appointments);
    return appointments
      .filter(appointment => appointment.status?.toUpperCase() === 'ONGOING')
      .map(appointment => ({
        ...appointment,
        patientName: appointment.userDetail ? 
          `${appointment.userDetail.FirstName || ''} ${appointment.userDetail.LastName || ''}`.trim() :
          `Patient ${appointment.userId?.slice(-8) || 'Unknown'}`,
        patientInfo: appointment.userDetail ? {
          age: appointment.userDetail.Age,
          gender: appointment.userDetail.Gender,
          email: appointment.userDetail.Email,
          phone: appointment.userDetail.Phone_Number,
          address: appointment.userDetail.Address,
          chronicConditions: appointment.userDetail.ChronicConditions || [],
          allergies: appointment.userDetail.Allergies || []
        } : null
      }))
      .sort((a, b) => {
        // Sort by start time (most recent first)
        const dateA = new Date(a.startedAt || a.confirmedSlot || 0);
        const dateB = new Date(b.startedAt || b.confirmedSlot || 0);
        return dateB - dateA;
      });
  }, []);

  const processCompletedAppointments = useCallback((appointments) => {
    console.log('Processing completed appointments:', appointments);
    return appointments
      .filter(appointment => appointment.status?.toUpperCase() === 'COMPLETED')
      .map(appointment => ({
        ...appointment,
        patientName: appointment.userDetail ? 
          `${appointment.userDetail.FirstName || ''} ${appointment.userDetail.LastName || ''}`.trim() :
          `Patient ${appointment.userId?.slice(-8) || 'Unknown'}`,
        patientInfo: appointment.userDetail ? {
          age: appointment.userDetail.Age,
          gender: appointment.userDetail.Gender,
          email: appointment.userDetail.Email,
          phone: appointment.userDetail.Phone_Number,
          address: appointment.userDetail.Address,
          chronicConditions: appointment.userDetail.ChronicConditions || [],
          allergies: appointment.userDetail.Allergies || []
        } : null
      }));
  }, []);

  // Fetch appointments data
  const fetchScheduledAppointments = useCallback(async () => {
    if (!user || !user.username) {
      setScheduledAppointments([]);
      return;
    }
    
    try {
      console.log('=== FETCHING SCHEDULED APPOINTMENTS FOR DOCTOR ===');
      console.log('Doctor ID:', user.username);
      
      const response = await fetchAppointmentsByDoctor(
        user.username, // Use same pattern as Chronic Prescriptions
        ['SCHEDULED'], 
        true, // includeUserData
        false // includePlanData
      );
      
      const appointments = response.items || [];
      console.log('Raw scheduled appointments:', appointments);
      
      const processed = processScheduledAppointments(appointments);
      console.log('Processed scheduled appointments:', processed);
      
      setScheduledAppointments(processed);
    } catch (error) {
      console.error('Error fetching scheduled appointments:', error);
      throw error;
    }
  }, [user, processScheduledAppointments]);

  const fetchUpcomingAppointments = useCallback(async () => {
    if (!user || !user.username) {
      setUpcomingAppointments([]);
      return;
    }
    
    try {
      console.log('=== FETCHING UPCOMING APPOINTMENTS FOR DOCTOR (CURRENT WEEK) ===');
      console.log('Doctor ID:', user.username);
      
      const response = await fetchAppointmentsByDoctor(
        user.username, // Use same pattern as Chronic Prescriptions
        ['CONFIRMED'], 
        true, // includeUserData
        false // includePlanData
      );
      
      const appointments = response.items || [];
      console.log('Raw confirmed appointments:', appointments);
      
      const processed = processUpcomingAppointments(appointments);
      console.log('Processed upcoming appointments (current week):', processed);
      
      setUpcomingAppointments(processed);
    } catch (error) {
      console.error('Error fetching upcoming appointments:', error);
      throw error;
    }
  }, [user, processUpcomingAppointments]);

  const fetchOngoingAppointments = useCallback(async () => {
    if (!user || !user.username) {
      setOngoingAppointments([]);
      return;
    }
    
    try {
      console.log('=== FETCHING ONGOING APPOINTMENTS FOR DOCTOR ===');
      console.log('Doctor ID:', user.username);
      
      const response = await fetchAppointmentsByDoctor(
        user.username, // Use same pattern as Chronic Prescriptions
        ['ONGOING'], 
        true, // includeUserData
        false // includePlanData
      );
      
      const appointments = response.items || [];
      console.log('Raw ongoing appointments:', appointments);
      
      const processed = processOngoingAppointments(appointments);
      console.log('Processed ongoing appointments:', processed);
      
      setOngoingAppointments(processed);
    } catch (error) {
      console.error('Error fetching ongoing appointments:', error);
      throw error;
    }
  }, [user, processOngoingAppointments]);

  const fetchCompletedAppointments = useCallback(async () => {
    if (!user || !user.username) {
      setCompletedAppointments([]);
      return;
    }
    
    try {
      console.log('=== FETCHING COMPLETED APPOINTMENTS FOR DOCTOR ===');
      console.log('Doctor ID:', user.username);
      
      const response = await fetchAppointmentsByDoctor(
        user.username, // Use same pattern as Chronic Prescriptions
        ['COMPLETED'], 
        true, // includeUserData
        false // includePlanData
      );
      
      const appointments = response.items || [];
      console.log('Raw completed appointments:', appointments);
      
      const processed = processCompletedAppointments(appointments);
      console.log('Processed completed appointments:', processed);
      
      setCompletedAppointments(processed);
    } catch (error) {
      console.error('Error fetching completed appointments:', error);
      throw error;
    }
  }, [user, processCompletedAppointments]);

  // Load appointments based on active tab
  const loadAppointments = useCallback(async () => {
    // Don't load if we don't have a valid doctor ID - same validation as Chronic Prescriptions
    if (!user || !user.username) {
      console.log('No valid user or username available, skipping appointment load');
      setError('Unable to load appointments: No valid doctor ID');
      return;
    }

      setLoading(true);
      setError(null);
      
      try {
      console.log('=== LOADING APPOINTMENTS FOR DOCTOR ===');
      console.log('Doctor ID:', user.username);
      
      switch (activeTab) {
        case 0: // Scheduled
          await fetchScheduledAppointments();
          break;
        case 1: // Upcoming (Current Week)
          await fetchUpcomingAppointments();
          break;
        case 2: // Ongoing
          await fetchOngoingAppointments();
          break;
        case 3: // Completed
          await fetchCompletedAppointments();
          break;
        case 4: // Calendar - load all appointments
          await Promise.all([
            fetchScheduledAppointments(),
            fetchUpcomingAppointments(),
            fetchOngoingAppointments(),
            fetchCompletedAppointments()
          ]);
          break;
        default:
          break;
      }
      } catch (err) {
      console.error('Error loading appointments:', err);
      setError(err.message || 'Failed to load appointments');
    } finally {
        setLoading(false);
      }
  }, [activeTab, fetchScheduledAppointments, fetchUpcomingAppointments, fetchOngoingAppointments, fetchCompletedAppointments, user]);

  // Initial load
  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      loadAppointments();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [loadAppointments]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle appointment confirmation
  const handleConfirmAppointment = async (requestId) => {
    try {
      setLoading(true);
      
      // Find the appointment to confirm
      const appointment = scheduledAppointments.find(app => app.requestId === requestId);
      if (!appointment) {
        throw new Error('Appointment not found');
      }

      // Confirm the appointment with a slot (using current time + 1 hour as example)
      const confirmedSlot = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      
      await confirmAppointment({
        userId: appointment.userId,
        requestId: appointment.requestId,
        assignedDoctorId: currentDoctor.id,
        confirmedSlot: confirmedSlot
      });

      setSnackbar({
        open: true,
        message: 'Appointment confirmed successfully!',
        severity: 'success'
      });

      // Refresh data
      await loadAppointments();
    } catch (error) {
      console.error('Error confirming appointment:', error);
      setSnackbar({
        open: true,
        message: `Failed to confirm appointment: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle appointment completion
  const handleCompleteAppointment = async (appointment) => {
    try {
      setLoading(true);
      
      await completeAppointment({
        userId: appointment.userId,
        requestId: appointment.requestId,
        doctorComments: 'Consultation completed successfully'
      });

      setSnackbar({
        open: true,
        message: t.privatePractice.appointmentCompleted,
        severity: 'success'
      });

      // Refresh data
      await loadAppointments();
    } catch (error) {
      console.error('Error completing appointment:', error);
      setSnackbar({
        open: true,
        message: `Failed to complete appointment: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle video call start
  const handleStartVideoCall = async (appointment) => {
    console.log('Starting video call for appointment:', appointment);
    
    try {
      // For CONFIRMED appointments, start the video call (this creates room and updates status)
      if (appointment.status?.toUpperCase() === 'CONFIRMED') {
        console.log('Starting video call (this will create room and change status to ONGOING)...');
        await startVideoCall({
          userId: appointment.userId,
          requestId: appointment.requestId,
          assignedDoctorId: currentDoctor.id
        });
        
        setSnackbar({
          open: true,
          message: 'Video call started successfully!',
          severity: 'success'
        });
        
        // Refresh appointments to get updated status
        await loadAppointments();
      } else if (appointment.status?.toUpperCase() === 'ONGOING') {
        console.log('Joining existing video call...');
        setSnackbar({
          open: true,
          message: 'Joining video call...',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('Error with video call:', error);
      let errorMessage = 'Failed to start video call';
      
      if (error.message.includes('Doctor not assigned')) {
        errorMessage = 'You are not assigned to this appointment';
      } else if (error.message.includes('not found')) {
        errorMessage = 'Appointment not found';
      } else {
        errorMessage = `Failed to start video call: ${error.message}`;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return; // Don't start video call if there's an error
    }
    
    console.log('Setting video call appointment:', appointment);
    setVideoCallAppointment(appointment);
    console.log('Setting showVideoCall to true');
    setShowVideoCall(true);
  };

  // Handle video call end
  const handleEndVideoCall = () => {
    console.log('Ending video call, setting showVideoCall to false');
    setShowVideoCall(false);
    setVideoCallAppointment(null);
  };

  // Handle view appointment details
  const handleViewAppointmentDetails = async (appointment) => {
    try {
      console.log('Fetching detailed appointment data with plan information...');
      setLoading(true);
      
      // Fetch the appointment with plan data included
      const detailedAppointment = await fetchAppointmentById(
        currentDoctor.id, // doctor ID
        appointment.requestId,
        true, // includeUserData
        true  // includePlanData
      );
      
      console.log('Detailed appointment data:', detailedAppointment);
      setSelectedAppointment(detailedAppointment);
      setShowAppointmentDetail(true);
    } catch (error) {
      console.error('Error fetching appointment details:', error);
      setSnackbar({
        open: true,
        message: `Failed to load appointment details: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle back from appointment details
  const handleBackFromAppointmentDetails = () => {
    setShowAppointmentDetail(false);
    setSelectedAppointment(null);
  };

  // Handle appointment update from detail view
  const handleAppointmentUpdateFromDetail = () => {
    // Refresh appointments data after update
    loadAppointments();
    // Optionally close detail view
    setShowAppointmentDetail(false);
    setSelectedAppointment(null);
  };

  // Sort appointments
  const sortAppointments = (appointments) => {
    const sorted = [...appointments];
    
    switch (sortBy) {
      case 'date':
        return sorted.sort((a, b) => new Date(b.requestedAt || b.createdAt || 0) - new Date(a.requestedAt || a.createdAt || 0));
      case 'patient':
        return sorted.sort((a, b) => (a.patientName || '').localeCompare(b.patientName || ''));
      case 'status':
        return sorted.sort((a, b) => (a.status || '').localeCompare(b.status || ''));
      default:
        return sorted;
    }
  };

  // Get current appointments based on active tab
  const getCurrentAppointments = () => {
    switch (activeTab) {
      case 0:
        return sortAppointments(scheduledAppointments);
      case 1:
        return sortAppointments(upcomingAppointments);
      case 2:
        return sortAppointments(ongoingAppointments);
      case 3:
        return sortAppointments(completedAppointments);
      case 4: // Calendar view - return all appointments
        return getAllAppointments();
      default:
        return [];
    }
  };

  // Get all appointments for calendar view
  const getAllAppointments = () => {
    return [
      ...scheduledAppointments,
      ...upcomingAppointments,
      ...ongoingAppointments,
      ...completedAppointments
    ].sort((a, b) => {
      const dateA = new Date(a.confirmedSlot || a.scheduledAt || a.createdAt || 0);
      const dateB = new Date(b.confirmedSlot || b.scheduledAt || b.createdAt || 0);
      return dateB - dateA; // Most recent first
    });
  };

  // Get tab statistics
  const getTabStats = () => ({
    scheduled: scheduledAppointments.length,
    upcoming: upcomingAppointments.length,
    ongoing: ongoingAppointments.length,
    completed: completedAppointments.length,
    total: getAllAppointments().length
  });

  const stats = getTabStats();

  if (showVideoCall && videoCallAppointment) {
    console.log('Rendering TwilioVideo component with appointment:', videoCallAppointment);
    return (
      <TwilioVideo 
        appointment={videoCallAppointment}
        onEndCall={handleEndVideoCall}
        doctorName={currentDoctor.name}
        autoStart={true}
      />
    );
  }

  if (showAppointmentDetail && selectedAppointment) {
    return (
      <AppointmentDetail
        appointment={selectedAppointment}
        onBack={handleBackFromAppointmentDetails}
        onAppointmentUpdate={handleAppointmentUpdateFromDetail}
        onStartVideoCall={handleStartVideoCall}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t.privatePractice.title}
            </Typography>
              <Typography variant="h6" color="text.secondary">
              {t.privatePractice.subtitle}
              </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {currentDoctor.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentDoctor.specialty}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <EventNoteIcon sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                {stats.scheduled}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t.privatePractice.scheduledAppointments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                {stats.upcoming}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t.privatePractice.upcomingAppointments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PlayCircleFilledIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                {stats.ongoing}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t.privatePractice.ongoingAppointments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t.privatePractice.completedAppointments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t.privatePractice.sortBy}</InputLabel>
            <Select
              value={sortBy}
              label={t.privatePractice.sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="date">{t.privatePractice.date}</MenuItem>
              <MenuItem value="patient">{t.privatePractice.patientName}</MenuItem>
              <MenuItem value="status">{t.privatePractice.status}</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAppointments}
            disabled={loading}
          >
            {t.privatePractice.refresh}
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab 
            icon={<EventNoteIcon />} 
            label={`${t.privatePractice.scheduledAppointments} (${stats.scheduled})`}
            sx={{ color: theme.palette.warning.main }}
          />
          <Tab 
            icon={<ScheduleIcon />} 
            label={`${t.privatePractice.upcomingAppointments} (${stats.upcoming})`}
            sx={{ color: theme.palette.success.main }}
          />
          <Tab 
            icon={<PlayCircleFilledIcon />} 
            label={`${t.privatePractice.ongoingAppointments} (${stats.ongoing})`}
            sx={{ color: theme.palette.primary.main }}
          />
          <Tab 
            icon={<AssignmentIcon />} 
            label={`${t.privatePractice.completedAppointments} (${stats.completed})`}
            sx={{ color: theme.palette.info.main }}
          />
          <Tab 
            icon={<CalendarIcon />} 
            label={`${t.privatePractice.calendarView} (${stats.total})`}
            sx={{ color: theme.palette.secondary.main }}
          />
        </Tabs>
      </Paper>
      
      {/* Content */}
      <Box>
        {activeTab === 0 && (
          <AppointmentList
            appointments={getCurrentAppointments()}
            loading={loading}
            error={error}
            onConfirm={handleConfirmAppointment}
            onComplete={handleCompleteAppointment}
            onStartVideoCall={handleStartVideoCall}
            onViewDetails={handleViewAppointmentDetails}
            emptyMessage={t.privatePractice.noScheduledAppointments}
            emptySubMessage={t.privatePractice.noScheduledSubMessage}
          />
        )}
        
        {activeTab === 1 && (
          <AppointmentList
            appointments={getCurrentAppointments()}
            loading={loading}
            error={error}
            onConfirm={handleConfirmAppointment}
            onComplete={handleCompleteAppointment}
            onStartVideoCall={handleStartVideoCall}
            onViewDetails={handleViewAppointmentDetails}
            emptyMessage={t.privatePractice.noUpcomingAppointments}
            emptySubMessage={t.privatePractice.noUpcomingSubMessage}
          />
        )}
        
        {activeTab === 2 && (
          <AppointmentList
            appointments={getCurrentAppointments()}
            loading={loading}
            error={error}
            onConfirm={handleConfirmAppointment}
            onComplete={handleCompleteAppointment}
            onStartVideoCall={handleStartVideoCall}
            onViewDetails={handleViewAppointmentDetails}
            emptyMessage={t.privatePractice.noOngoingAppointments}
            emptySubMessage={t.privatePractice.noOngoingSubMessage}
          />
        )}
        
        {activeTab === 3 && (
          <AppointmentList
            appointments={getCurrentAppointments()}
            loading={loading}
            error={error}
            onConfirm={handleConfirmAppointment}
            onComplete={handleCompleteAppointment}
            onStartVideoCall={handleStartVideoCall}
            onViewDetails={handleViewAppointmentDetails}
            emptyMessage={t.privatePractice.noCompletedAppointments}
            emptySubMessage={t.privatePractice.noCompletedSubMessage}
          />
        )}
        
        {activeTab === 4 && (
          <AppointmentCalendar
            appointments={getCurrentAppointments()}
            onSelectEvent={handleViewAppointmentDetails}
            onStartVideoCall={handleStartVideoCall}
            onViewDetails={handleViewAppointmentDetails}
            language={language}
            t={t}
          />
        )}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PrivateHealthPracticeHub; 